import {BaseApi} from './BaseApi'
import {sanitizePayload} from '../utils/apiUtils'
import {MemberAccessRoleType} from '../utils/permissions'
import {reportCatchError} from '../utils/errorReporter'

// Enhanced User Management DTOs aligned with backend
export interface ModulePermissionRequest {
  key: string
  name: string
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  submodules?: ModulePermissionRequest[]
}

/**
 * Agency assignment with per-agency permissions for multi-agency users
 */
export interface AgencyAssignmentRequest {
  agencyId: string
  modulePermissions?: ModulePermissionRequest[]
}

/**
 * Agency assignment response (includes agency name for display)
 */
export interface AgencyAssignmentResponse {
  agencyId: string
  agencyName?: string
  modulePermissions?: ModulePermissionRequest[]
  /**
   * The user's manager on this agency ("Reports to"). Null when none is set; absent from a server
   * older than the field. Set it with setReportsTo.
   */
  reportsToUserId?: string | null
}

export interface UserOnboardRequest {
  firstName: string
  lastName: string
  age?: number
  phoneNumber?: string
  address?: {
    display?: string
    street?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
  }
  emailAddress: string
  roles: string[]
  password?: string
  agreedToTermsDate?: Date | string
  agencyId?: string
  companyId?: string
  agencyIds?: string[]
  // Location assignment (for Company roles)
  regionId?: string
  siteId?: string
  departmentId?: string
  department?: string
  // Custom permissions override - DEPRECATED: Use agencyAssignments for Agency/MSP roles
  modulePermissions?: ModulePermissionRequest[]
  // Multi-agency assignments with per-agency permissions (for Agency/MSP roles)
  agencyAssignments?: AgencyAssignmentRequest[]
}

// Legacy interfaces for backward compatibility - now simplified
export interface OnboardAgencyAdminRequest {
  firstName: string
  lastName: string
  emailAddress: string
  agencyId: string
  password?: string
}

export interface OnboardGeneralAdminRequest {
  firstName: string
  lastName: string
  emailAddress: string
  agencyId: string
  password?: string
}

export interface OnboardCompanyAdminRequest {
  firstName: string
  lastName: string
  emailAddress: string
  companyId: string
  password?: string
  role?: string
}

export interface UserResponse {
  id: string
  email: string
  emailAddress?: string // API returns this field name
  firstName: string
  lastName: string
  memberAccessRole: MemberAccessRoleType
  agencyId?: string
  companyId?: string
  isActive: boolean
  createdDate: string
  modifiedDate?: string
  lastLoginDate?: string
  // Role and status fields
  roles?: string[]
  status?: 'Pending' | 'Active' | 'Disabled'
  // General Admin agency access
  agencyIds?: string[]
  // Location fields (for Company roles)
  regionId?: string
  siteId?: string
  departmentId?: string
  // Permission overrides (legacy single-agency)
  modulePermissions?: ModulePermissionRequest[]
  // Multi-agency assignments (for Agency/MSP roles)
  agencyAssignments?: AgencyAssignmentResponse[]
}

export interface GetUserByEmailRequest {
  email: string
}

export interface AddClaimsByUserEmailRequest {
  email: string
  claims: string[]
}

export interface InitiatePasswordResetRequest {
  email: string
}

export interface TemporaryPasswordResetRequest {
  email: string
  temporaryPassword: string
  newPassword: string
}

export interface ConfirmPasswordResetRequest {
  email: string
  confirmationCode: string
  newPassword: string
}

export interface WelcomeEmailRequest {
  userId: string
  email: string
  firstName: string
  lastName: string
  temporaryPassword?: string
}

export interface MemberAccessRequest {
  userId: string
  entityId: string
  roleType: MemberAccessRoleType
  accessLevel: 'read' | 'write' | 'admin'
}

export interface MemberAccessResponse {
  id: string
  userId: string
  entityId: string
  roleType: MemberAccessRoleType
  accessLevel: string
  isActive: boolean
  createdDate: string
}

export interface UserPagingModel {
  users: UserResponse[]
  totalCount: number
  pageSize: number
  pageNumber: number
  totalPages: number
}

/**
 * Response from checking if a user exists by email
 */
export interface CheckEmailResponse {
  exists: boolean
  userId?: string
  firstName?: string
  lastName?: string
  role?: string
  /** Every role of the existing user (STFFLG-4243: an agency added to them is capped by all of them) */
  roles?: string[]
  hasAgencyAccess?: boolean
}

/**
 * Request to add an existing user to an agency
 */
export interface AddUserToAgencyRequest {
  userId: string
  agencyId: string
  modulePermissions?: ModulePermissionRequest[]
}

export class UserApiService extends BaseApi {
  protected basePath = '/user'

  // New consolidated onboarding endpoint
  async onboardUser(request: UserOnboardRequest): Promise<UserResponse> {
    try {
      const sanitizedRequest = sanitizePayload(request)
      const response = await this.client.post<UserResponse>(`/user/onboardUser`, sanitizedRequest)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error onboarding user:', error)
      throw error
    }
  }

  // Helper functions for specific user types using the consolidated endpoint
  async onboardPlatformAdmin(request: {
    firstName: string
    lastName: string
    emailAddress: string
    password?: string
  }): Promise<UserResponse> {
    return this.onboardUser({
      ...request,
      roles: ['platformadmin'],
    })
  }

  async onboardGeneralAdmin(request: {
    firstName: string
    lastName: string
    emailAddress: string
    agencyId: string
    password?: string
  }): Promise<UserResponse> {
    return this.onboardUser({
      ...request,
      roles: ['general_admin'],
    })
  }

  async onboardMainAgencyAdmin(request: {
    firstName: string
    lastName: string
    emailAddress: string
    agencyId: string
    password?: string
  }): Promise<UserResponse> {
    return this.onboardUser({
      ...request,
      roles: ['mainagencyadmin'],
    })
  }

  async onboardAgencyAdmin(request: {
    firstName: string
    lastName: string
    emailAddress: string
    agencyId: string
    password?: string
  }): Promise<UserResponse> {
    return this.onboardUser({
      ...request,
      roles: ['agencyadmin'],
    })
  }

  async onboardCompanyAdmin(request: {
    firstName: string
    lastName: string
    emailAddress: string
    companyId: string
    password?: string
  }): Promise<UserResponse> {
    return this.onboardUser({
      ...request,
      roles: ['companyadmin'],
    })
  }

  async onboardCompanyUser(request: {
    firstName: string
    lastName: string
    emailAddress: string
    companyId: string
    roles: string[]
    password?: string
  }): Promise<UserResponse> {
    return this.onboardUser(request)
  }

  async onboardAgencyUser(request: {
    firstName: string
    lastName: string
    emailAddress: string
    agencyId: string
    roles: string[]
    password?: string
  }): Promise<UserResponse> {
    return this.onboardUser(request)
  }

  // User lookup and management
  async getUserIdByEmail(request: GetUserByEmailRequest): Promise<UserResponse> {
    try {
      const response = await this.client.post<UserResponse>(`/user/getUserIdByEmail`, request)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error getting user by email:', error)
      throw error
    }
  }

  async getUserById(userId: string): Promise<UserResponse> {
    try {
      const response = await this.client.get<UserResponse>(`/user/${userId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error getting user by ID:', error)
      throw error
    }
  }

  async getAllUsers(): Promise<UserResponse[]> {
    try {
      const response = await this.client.get<UserResponse[]>('/user/getAllUsers')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error getting all users:', error)
      throw error
    }
  }

  // Claims management
  async addClaimsByUserEmail(request: AddClaimsByUserEmailRequest): Promise<void> {
    try {
      await this.client.post('/user/addClaimsByUserEmail', request)
    } catch (error: unknown) {
      reportCatchError('Error adding claims by user email:', error)
      throw error
    }
  }

  // Password reset flow
  async initiatePasswordReset(request: InitiatePasswordResetRequest): Promise<void> {
    try {
      await this.client.post('/user/initiatePasswordReset', request)
    } catch (error: unknown) {
      reportCatchError('Error initiating password reset:', error)
      throw error
    }
  }

  async temporaryPasswordReset(request: TemporaryPasswordResetRequest): Promise<void> {
    try {
      await this.client.post('/user/temporaryPasswordReset', request)
    } catch (error: unknown) {
      reportCatchError('Error with temporary password reset:', error)
      throw error
    }
  }

  async confirmPasswordReset(request: ConfirmPasswordResetRequest): Promise<void> {
    try {
      await this.client.post('/user/confirmPasswordReset', request)
    } catch (error: unknown) {
      reportCatchError('Error confirming password reset:', error)
      throw error
    }
  }

  // Welcome emails
  async sendWelcomeEmail(request: WelcomeEmailRequest): Promise<void> {
    try {
      await this.client.post('/user/sendWelcomeEmail', request)
    } catch (error: unknown) {
      reportCatchError('Error sending welcome email:', error)
      throw error
    }
  }

  // Member access control
  async addMemberAccess(request: MemberAccessRequest): Promise<MemberAccessResponse> {
    try {
      const response = await this.client.post<MemberAccessResponse>(
        '/user/addMemberAccess',
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error adding member access:', error)
      throw error
    }
  }

  async removeMemberAccess(userId: string, entityId: string): Promise<void> {
    try {
      await this.client.delete(`/user/removeMemberAccess/${userId}/${entityId}`)
    } catch (error: unknown) {
      reportCatchError('Error removing member access:', error)
      throw error
    }
  }

  async getUserMemberAccess(userId: string): Promise<MemberAccessResponse[]> {
    try {
      const response = await this.client.get<MemberAccessResponse[]>(
        `/user/getMemberAccess/${userId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error getting user member access:', error)
      throw error
    }
  }

  // User status management
  async activateUser(userId: string): Promise<void> {
    try {
      await this.client.patch(`/user/activate/${userId}`)
    } catch (error: unknown) {
      reportCatchError('Error activating user:', error)
      throw error
    }
  }

  // Update user profile
  async updateUser(userId: string, updates: Partial<UserResponse>): Promise<UserResponse> {
    try {
      const sanitizedUpdates = sanitizePayload({...updates, id: userId})
      const response = await this.client.put<UserResponse>('/user', sanitizedUpdates)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating user:', error)
      throw error
    }
  }

  /**
   * Check if a user exists by email and whether they have access to a specific agency.
   * Used by the Add User page to determine if adding a new user or an existing user to an agency.
   * GET /user/checkEmail/:email
   */
  async checkUserEmail(email: string, agencyId?: string): Promise<CheckEmailResponse> {
    try {
      const response = await this.client.get<CheckEmailResponse>(
        `/user/checkEmail/${encodeURIComponent(email)}`,
        {
          params: agencyId ? {agencyId} : undefined,
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error checking user email:', error)
      throw error
    }
  }

  /**
   * Add an existing user to an agency with specific permissions.
   * Used when a user already exists in the system and needs to be granted access to another agency.
   * POST /user/addToAgency
   */
  async addUserToAgency(
    userId: string,
    agencyId: string,
    modulePermissions?: ModulePermissionRequest[],
  ): Promise<UserResponse> {
    try {
      const request: AddUserToAgencyRequest = {
        userId,
        agencyId,
        modulePermissions,
      }
      const response = await this.client.post<UserResponse>('/user/addToAgency', request)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error adding user to agency:', error)
      throw error
    }
  }

  /**
   * Remove a user's access from a specific agency.
   * DELETE /user/:userId/agency/:agencyId
   */
  async removeUserFromAgency(userId: string, agencyId: string): Promise<UserResponse> {
    try {
      const response = await this.client.delete<UserResponse>(`/user/${userId}/agency/${agencyId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error removing user from agency:', error)
      throw error
    }
  }

  /**
   * Get a user's permissions for a specific agency.
   * GET /user/:userId/agency/:agencyId/permissions
   */
  async getUserAgencyPermissions(
    userId: string,
    agencyId: string,
  ): Promise<ModulePermissionRequest[]> {
    try {
      const response = await this.client.get<ModulePermissionRequest[]>(
        `/user/${userId}/agency/${agencyId}/permissions`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching user agency permissions:', error)
      throw error
    }
  }

  /**
   * Update a user's permissions for a specific agency.
   * PUT /user/:userId/agency/:agencyId/permissions
   */
  async updateUserAgencyPermissions(
    userId: string,
    agencyId: string,
    modulePermissions: ModulePermissionRequest[],
  ): Promise<UserResponse> {
    try {
      const response = await this.client.put<UserResponse>(
        `/user/${userId}/agency/${agencyId}/permissions`,
        {modulePermissions},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating user agency permissions:', error)
      throw error
    }
  }

  /**
   * Set or clear who a user reports to on one agency. Null clears it.
   * PUT /user/:userId/agency/:agencyId/reports-to
   *
   * A server without the route answers Nest's 404 "Cannot PUT …", which is rethrown as is so the
   * caller can tell it apart with isRouteNotFound(error, 'PUT') from teamApi.
   */
  async setReportsTo(
    userId: string,
    agencyId: string,
    reportsToUserId: string | null,
  ): Promise<void> {
    try {
      await this.client.put(`/user/${userId}/agency/${agencyId}/reports-to`, {reportsToUserId})
    } catch (error: unknown) {
      reportCatchError('Error setting reports-to:', error)
      throw error
    }
  }

  /**
   * Get user by email
   * GET /user/getUserByEmail/:email
   */
  async getUserByEmail(email: string): Promise<UserResponse> {
    try {
      const response = await this.client.get<UserResponse>(`/user/getUserByEmail/${email}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching user by email:', error)
      throw error
    }
  }

  /**
   * Reset user password
   * POST /user/resetPassword
   */
  async resetUserPassword(email: string): Promise<{success: boolean; message: string}> {
    try {
      const response = await this.client.post<{success: boolean; message: string}>(
        '/user/resetPassword',
        {
          email,
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error resetting user password:', error)
      throw error
    }
  }

  /**
   * Change user password
   * POST /user/changePassword
   */
  async changeUserPassword(
    userId: string,
    currentPassword: string,
    newPassword: string,
  ): Promise<{success: boolean; message: string}> {
    try {
      const response = await this.client.post<{success: boolean; message: string}>(
        '/user/changePassword',
        {
          userId,
          currentPassword,
          newPassword,
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error changing user password:', error)
      throw error
    }
  }

  /**
   * Verify user password
   * POST /user/verifyUserPassword
   */
  async verifyUserPassword(password: string): Promise<{success: boolean; message: string}> {
    try {
      const response = await this.client.post<{success: boolean; message: string}>(
        '/user/verifyUserPassword',
        {
          password: decodeURIComponent(password),
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error verifying user password:', error)
      throw error
    }
  }

  /**
   * Update user password (for password change flow)
   * PUT /user/updateUserPassword
   */
  async updateUserPassword(password: string): Promise<{success: boolean; message: string}> {
    try {
      const response = await this.client.put<{success: boolean; message: string}>(
        '/user/updateUserPassword',
        {
          password: decodeURIComponent(password),
        },
        {
          params: {
            application: 'MSP',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating user password:', error)
      throw error
    }
  }

  /**
   * Per-user UI preferences (e.g. the saved dashboard layout).
   * GET /user/dashboard-preferences — resilient by default: returns {} on failure so the UI falls
   * back to defaults.
   *
   * `throwOnError: true` is for a caller about to write the blob back: it rejects on a failed read,
   * and on a response that is not a plain object, instead of answering {}. The PUT replaces the
   * WHOLE blob, so a key merged into a {} that stands for a failed read would wipe every saved
   * dashboard layout (docs/deep-dive/dashboard-shared.md §4).
   */
  async getDashboardPreferences(
    options: {throwOnError?: boolean} = {},
  ): Promise<Record<string, unknown>> {
    try {
      const response = await this.client.get<Record<string, unknown>>('/user/dashboard-preferences')
      if (!options.throwOnError) return response.data ?? {}
      const data: unknown = response.data
      if (typeof data !== 'object' || data === null || Array.isArray(data)) {
        throw new Error('Unexpected dashboard preferences response: not an object')
      }
      return data as Record<string, unknown>
    } catch (error: unknown) {
      reportCatchError('Error fetching dashboard preferences:', error)
      if (options.throwOnError) throw error
      return {}
    }
  }

  /** PUT /user/dashboard-preferences — persist the per-user preferences blob. */
  async saveDashboardPreferences(preferences: Record<string, unknown>): Promise<void> {
    try {
      await this.client.put('/user/dashboard-preferences', {preferences})
    } catch (error: unknown) {
      reportCatchError('Error saving dashboard preferences:', error)
      throw error
    }
  }

  /**
   * Update agreed to terms date
   * PUT /user/updateAgreedToTermsDate
   */
  async updateAgreedToTermsDate(date: Date | string): Promise<UserResponse> {
    try {
      const response = await this.client.put<UserResponse>(
        '/user/updateAgreedToTermsDate',
        {},
        {
          params: {
            date: date,
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating agreed to terms date:', error)
      throw error
    }
  }

  /**
   * Deactivate user account
   * PUT /user/deactivateUser/:id
   */
  async deactivateUser(userId: string): Promise<{success: boolean; message: string}> {
    try {
      const response = await this.client.put<{success: boolean; message: string}>(
        `/user/deactivateUser/${userId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error deactivating user:', error)
      throw error
    }
  }

  /**
   * Reactivate user account
   * PUT /user/reactivateUser/:id
   */
  async reactivateUser(userId: string): Promise<{success: boolean; message: string}> {
    try {
      const response = await this.client.put<{success: boolean; message: string}>(
        `/user/reactivateUser/${userId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error reactivating user:', error)
      throw error
    }
  }

  /**
   * Get user activity log
   * GET /user/getUserActivityLog/:id
   */
  async getUserActivityLog(
    userId: string,
    pageNumber: number = 1,
    pageSize: number = 10,
  ): Promise<{
    activities: Array<{
      id: string
      action: string
      timestamp: Date
      details: string
      ipAddress?: string
    }>
    totalCount: number
    currentPage: number
    pageSize: number
    totalPages: number
  }> {
    try {
      const response = await this.client.get(`/user/getUserActivityLog/${userId}`, {
        params: {pageNumber, pageSize},
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching user activity log:', error)
      throw error
    }
  }
}

export const userApi = new UserApiService()
